import { FC, useEffect, useRef, useState } from 'react';
import { Price, Product } from '@typings/graphql-models';
import useNoodleApi from '@hooks/useNoodleApi';
import getAvailabilityBlocks from '@tsClient/getAvailabilityBlocks';
import Calendar from '@components/Calendar';
import { useRouter } from 'next/router';
import CustomerTimeSelector from '@components/CustomerTimeSelector';
import { CartItem } from '@providers/ShoppingCart';
import * as format from '@format';
import { format as formatDate, addMinutes } from 'date-fns';
import DOMPurify from 'isomorphic-dompurify';

import Spacer from '@components/Spacer';
import { Booking } from '@typings/api-models';

import s from './CustomerSchedulingCalendar.module.scss';

type ProductPrice = (Pick<Price, 'sessionDuration' | 'sessionInterval'> &
CartItem & {
  priceDescription?: {
    html?: string | null;
  } | null;
});

type ThisProduct = Pick<Product, 'id'> & {
  prices: ProductPrice[];
  creator?: {
    timezone?: string | null;
    name?: string | null;
  } | null;
};

type Props = {
  product: ThisProduct;
  selectedDate?: string | null;
  isRescheduling?: boolean;
  booking?: Pick<Booking, 'id' | 'startAt' | 'priceId'>;
  onFinish?: (price: CartItem) => Promise<void>;
  onDateTimeSelect?: (props: { price: CartItem; sessionDuration: number; sessionTime: string }) => Promise<void>;
} & (
  | { isPreview: true; priceId?: never }
  | { isPreview?: false; priceId: string | null }
);

const formatTimeObjects = (
  input:
    | Array<{
        blocks: Array<{ startAt: string }>;
        blockDuration: number;
        schedulingInterval: number;
      }>
    | undefined,
): string => {
  if (!input) return '';

  const timeRanges: string[] = [];
  const startTimes: string[] = [];

  input.forEach(timeObj => {
    if (timeObj.blocks.length === 0) return;

    const formattedBlocks = timeObj.blocks.map(block => new Date(block.startAt));

    let rangeStart = formattedBlocks[0];
    let rangeEnd = addMinutes(rangeStart, timeObj.blockDuration);

    for (let i = 1; i < formattedBlocks.length; i += 1) {
      const currentBlock = formattedBlocks[i];
      const expectedNextBlock = addMinutes(formattedBlocks[i - 1], timeObj.schedulingInterval);

      if (currentBlock.getTime() !== expectedNextBlock.getTime()) {
        const startTime = formatDate(rangeStart, 'h:mm a');
        const endTime = formatDate(rangeEnd, 'h:mm a');
        const timeRange = `${startTime} to ${endTime}`;

        if (!timeRanges.includes(timeRange)) {
          timeRanges.push(timeRange);
          startTimes.push(startTime);
        }

        rangeStart = currentBlock;
      }

      rangeEnd = addMinutes(currentBlock, timeObj.blockDuration);
    }

    const startTime = formatDate(rangeStart, 'h:mm a');
    const endTime = formatDate(rangeEnd, 'h:mm a');
    const timeRange = `<strong>from ${startTime} to ${endTime}</strong>`;

    if (!timeRanges.includes(timeRange) && !startTimes.includes(startTime)) {
      timeRanges.push(timeRange);
      startTimes.push(startTime);
    }
  });

  return timeRanges.join(', ');
};

const CustomerSchedulingCalendar: FC<Props> = ({
  product,
  selectedDate,
  booking,
  onFinish,
  onDateTimeSelect,
  isPreview = false,
  priceId ,
}) => {
  const router = useRouter();
  const [year, setYear] = useState(selectedDate ? new Date(selectedDate).getFullYear() : new Date().getFullYear());
  const [month, setMonth] = useState(selectedDate ? new Date(selectedDate).getMonth() : new Date().getMonth());

  const { data, getData: getAvailabilityBlocksFn, fetchingState: availabilityFetchingState } = useNoodleApi(getAvailabilityBlocks);

  const timeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchMonth = format.twoDigits(month + 1);
    const fetchYear = year.toFixed(0);
    let duration = 0;
    let interval = 0;
    if (priceId) {
      const price = product.prices.find((p) => p.id === priceId);
      duration = price?.sessionDuration || 0;
      interval = price?.sessionInterval || 0;
    } else {
      product.prices
        .forEach(price => {
          if (!duration && price.sessionDuration && price.sessionInterval) {
            duration = price.sessionDuration;
            interval = price.sessionInterval;
          } else if (price.sessionDuration && price.sessionInterval && price.sessionDuration < duration) {
            duration = price.sessionDuration;
            interval = price.sessionInterval;
          }
        });
    }
    if (duration && interval) {
      getAvailabilityBlocksFn({
        blockData: [
          { blockDuration: duration, schedulingInterval: interval },
        ],
        month: fetchMonth,
        productId: product.id,
        year: fetchYear,
      });
    }
  }, [getAvailabilityBlocksFn, month, year, product, priceId]);

  const isDayBooked = (day: Date): boolean => {
    const dateString = format.datetime.dateString(day);
    return !data?.days?.find(d => dateString === d.date)?.hasAvailability;
  };

  const isDaySelected = (day: Date): boolean => {
    const dateString = format.datetime.dateString(day);
    return dateString === selectedDate;
  };

  const getPriceForTimeSelector = (thisProduct: ThisProduct): ProductPrice => {
    if (priceId) {
      const chosenPrice = thisProduct.prices.find(p => p.id === priceId);
      if (chosenPrice) {
        return chosenPrice;
      }
    }
    return thisProduct.prices[0];
  };

  const handleDayClick = (day: Date): void => {
    const routerBasePath = router.asPath.split('?');
    const selectedDateString = format.datetime.dateString(day);
    const queryParamsStr = new URLSearchParams({ selectedDate: selectedDateString }).toString();
    const urlWithNewTab = `${routerBasePath[0]}?${queryParamsStr}&${
      // keep only one selectedDate param
      routerBasePath[1]
        ? routerBasePath[1]
          .split('&')
          .filter(param => !param.includes('selectedDate'))
          .join('&')
        : ''
    }`;

    if (timeRef.current && !isPreview) {
      setTimeout(() => {
        timeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
    }
    router.push(urlWithNewTab, undefined, { shallow: true });
  };

  // setMonth to the month of the selectedDate
  useEffect(() => {
    if (selectedDate) {
      setMonth(new Date(selectedDate).getMonth());
    }
  }, [selectedDate]);

  return (
    <>
      <Spacer />
      <Calendar
        isLoading={availabilityFetchingState.isFetching}
        isDayBlocked={isDayBooked}
        isDaySelected={isDaySelected}
        initialMonth={month}
        onDayClick={handleDayClick}
        onMonthChange={setMonth}
        onYearChange={setYear}
      />
      <div ref={timeRef}>
        {selectedDate && !isPreview && product.prices.length > 0 && (
          <>
            <Spacer />
            <CustomerTimeSelector
              isLoading={availabilityFetchingState.isFetching}
              availability={data?.days.find(d => d.date === selectedDate)?.times}
              creatorTimezone={product.creator?.timezone || ''}
              price={getPriceForTimeSelector(product)}
              booking={booking}
              onFinish={onFinish}
              onDateTimeSelect={onDateTimeSelect}
            />
          </>
        )}
      </div>
      {selectedDate && isPreview && data?.days.find(d => d.date === selectedDate)?.times && (
        <>
          <div className={s.timesPreview}>
            <h2 className="action">
              {product.creator?.name}&apos;s {format.datetime.withoutTime(selectedDate)} availability
            </h2>
            <p
              className="body-sm"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  `${product.creator?.name} is available ${formatTimeObjects(data.days.find(d => d.date === selectedDate)?.times)}`,
                ),
              }}
            />
            {product.creator?.timezone && (
              <p className='body-sm'>
                All times in {format.datetime.abbreviatedTimezone({ timezone: product.creator.timezone })}
              </p>
            )}
            <small className="caption">Select an option below to schedule</small>
          </div>
        </>
      )}
    </>
  );
};

export default CustomerSchedulingCalendar;
